import {FC} from 'react';
import {Box, Typography} from '@mui/material';
import {useTranslation} from 'react-i18next';
import {IConfusionMatrix, IMetrics} from '../../../../../api/types/aiReview.type';
import {useGetGroundTruthEvaluation} from '../../../../../hooks/ai/review/useReview';
import ArrTooltipWrapper from '../../../../common/arr-tooltip-wrapper/ArrTooltipWrapper';
import {ReactComponent as InfoIcon} from '../../../../../assets/images/info-icon.svg';
import {ReactComponent as GroundTruthChevronDivider} from '../../../../../assets/images/ground-truth-chevron-divider.svg';
import {numberFormatter} from '../../../../../utils/utils';
import JobResultMetricsStrip, {
  JobResultMetricItem,
} from './JobResultMetricsStrip';
import './RelevancyGroundTruthMetrics.scss';

type RelevancyGroundTruthMetricsProps = {
  jobId?: string | number;
};

const formatCount = (value: number | undefined) =>
  numberFormatter.format(value ?? 0);

const toDisplayPercent = (value: number | undefined) => {
  const numeric = Number(value ?? 0);
  if (!Number.isFinite(numeric)) {
    return 0;
  }
  return numeric > 1 ? numeric : numeric * 100;
};

const formatPercent = (value: number | undefined) =>
  `${Math.round(toDisplayPercent(value))}%`;

const RelevancyGroundTruthMetrics: FC<RelevancyGroundTruthMetricsProps> = ({
  jobId,
}) => {
  const {t} = useTranslation();
  const {data: groundTruthData} = useGetGroundTruthEvaluation(
    String(jobId ?? '')
  );

  const confusionMatrix = groundTruthData?.confusionMatrix;
  const scores = groundTruthData?.metrics;
  const groundTruthName = groundTruthData?.groundTruthName ?? '';

  const countMetrics: {
    id: string;
    key: keyof IConfusionMatrix;
    labelKey: string;
    tooltipKey: string;
  }[] = [
    {
      id: 'true-positives',
      key: 'truePositives',
      labelKey: 'ai.review.groundTruthEvaluation.metrics.truePositives',
      tooltipKey:
        'ai.review.groundTruthEvaluation.metrics.truePositivesTooltipText',
    },
    {
      id: 'false-positives',
      key: 'falsePositives',
      labelKey: 'ai.review.groundTruthEvaluation.metrics.falsePositives',
      tooltipKey:
        'ai.review.groundTruthEvaluation.metrics.falsePositivesTooltipText',
    },
    {
      id: 'true-negatives',
      key: 'trueNegatives',
      labelKey: 'ai.review.groundTruthEvaluation.metrics.trueNegatives',
      tooltipKey:
        'ai.review.groundTruthEvaluation.metrics.trueNegativesTooltipText',
    },
    {
      id: 'false-negatives',
      key: 'falseNegatives',
      labelKey: 'ai.review.groundTruthEvaluation.metrics.falseNegatives',
      tooltipKey:
        'ai.review.groundTruthEvaluation.metrics.falseNegativesTooltipText',
    },
  ];

  const scoreMetrics: {
    id: string;
    key: keyof IMetrics;
    labelKey: string;
    tooltipKey: string;
  }[] = [
    {
      id: 'accuracy',
      key: 'accuracy',
      labelKey: 'ai.review.groundTruthEvaluation.scores.accuracy',
      tooltipKey: 'ai.review.groundTruthEvaluation.scores.accuracyTooltipText',
    },
    {
      id: 'precision',
      key: 'precision',
      labelKey: 'ai.review.groundTruthEvaluation.scores.precision',
      tooltipKey: 'ai.review.groundTruthEvaluation.scores.precisionTooltipText',
    },
    {
      id: 'recall',
      key: 'recall',
      labelKey: 'ai.review.groundTruthEvaluation.scores.recall',
      tooltipKey: 'ai.review.groundTruthEvaluation.scores.recallTooltipText',
    },
    {
      id: 'f1-score',
      key: 'f1Score',
      labelKey: 'ai.review.groundTruthEvaluation.scores.f1Score',
      tooltipKey: 'ai.review.groundTruthEvaluation.scores.f1ScoreTooltipText',
    },
  ];

  const countItems: JobResultMetricItem[] = countMetrics.map((metric) => ({
    id: metric.id,
    displayValue: formatCount(confusionMatrix?.[metric.key]),
    label: t(metric.labelKey),
    showInfoTooltip: true,
    tooltip: t(metric.tooltipKey),
    ariaLabel: `${t(metric.labelKey)} ${formatCount(confusionMatrix?.[metric.key])}`,
  }));

  const scoreItems: JobResultMetricItem[] = scoreMetrics.map((metric) => ({
    id: metric.id,
    displayValue: formatPercent(scores?.[metric.key]),
    label: t(metric.labelKey),
    showInfoTooltip: true,
    tooltip: t(metric.tooltipKey),
    ariaLabel: `${t(metric.labelKey)} ${formatPercent(scores?.[metric.key])}`,
  }));

  return (
    <Box
      className="relevancy-ground-truth-metrics"
      data-testid="relevancy-ground-truth-metrics"
    >
      <Box
        className="relevancy-ground-truth-metrics__header"
        data-testid="relevancy-ground-truth-metrics-header"
      >
        <Typography
          className="typo-text label3_size relevancy-ground-truth-metrics__label"
          component="span"
        >
          <span className="relevancy-ground-truth-metrics__label-text">
            {t('ai.review.groundTruthEvaluation.selectionLabel')}
          </span>
          <span className="relevancy-ground-truth-metrics__info">
            <ArrTooltipWrapper
              trigger={
                <span
                  className="relevancy-ground-truth-metrics__info-trigger"
                  data-testid="relevancy-ground-truth-metrics-selection-info"
                  tabIndex={0}
                  role="button"
                  aria-label="information icon"
                >
                  <InfoIcon
                    className="relevancy-ground-truth-metrics__info-icon"
                    aria-hidden="true"
                    focusable="false"
                  />
                </span>
              }
              title={t('ai.review.groundTruthEvaluation.description')}
            >
              {t('ai.review.groundTruthEvaluation.description')}
            </ArrTooltipWrapper>
          </span>
        </Typography>
        <Typography
          className="typo-text subhead3_size relevancy-ground-truth-metrics__name"
          component="p"
          data-testid="relevancy-ground-truth-metrics-name"
        >
          {groundTruthName}
        </Typography>
      </Box>
      <Box
        className="relevancy-ground-truth-metrics__metrics"
        data-testid="relevancy-ground-truth-metrics-body"
      >
        <JobResultMetricsStrip
          metrics={countItems}
          testId="relevancy-ground-truth-metrics-strip"
          className="issue-coding-job-result-metrics--ground-truth"
        />
        <Box
          className="relevancy-ground-truth-metrics__divider"
          data-testid="relevancy-ground-truth-metrics-divider"
          aria-hidden="true"
        >
          <GroundTruthChevronDivider focusable="false" />
        </Box>
        <JobResultMetricsStrip
          metrics={scoreItems}
          testId="relevancy-ground-truth-metrics-scores"
          className="issue-coding-job-result-metrics--ground-truth"
        />
      </Box>
    </Box>
  );
};

export default RelevancyGroundTruthMetrics;
